import React, { useEffect, useRef, useState } from "react";
import Rating from "@mui/material/Rating";
import { useQuery } from "@apollo/client";
import { QUERY_REVIEWS } from "../../utils/GraphQL/queries";
import "./reviewsCarouselV2.css";

const AUTO_MS = 7000;

const prefersReducedMotion = () => {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
};

/**
 * Real cycling customer reviews (reuses QUERY_REVIEWS / getReviews — same data
 * source as prod ReviewCarousel). Auto-advances unless reduced-motion is set or
 * the user hovers. Themed via v2 CSS variables.
 */
const ReviewsCarouselV2 = () => {
  const { data } = useQuery(QUERY_REVIEWS);
  const reviews = data?.getReviews || [];
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  const count = reviews.length;
  const go = (n) => setIdx((i) => (count ? (n + count) % count : 0));

  useEffect(() => {
    if (!count || paused || prefersReducedMotion()) return undefined;
    timer.current = setInterval(() => {
      setIdx((i) => (i + 1) % count);
    }, AUTO_MS);
    return () => clearInterval(timer.current);
  }, [count, paused]);

  if (!count) return null;

  const r = reviews[idx];

  return (
    <div
      className="bdrv2-reviews"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="bdrv2-reviews-card bdrv2-glass" aria-live="polite">
        <span className="bdrv2-reviews-quote" aria-hidden="true">
          &ldquo;
        </span>
        <p className="bdrv2-reviews-text">{r?.review}</p>
        <div className="bdrv2-reviews-foot">
          <span className="bdrv2-reviews-name">{r?.customerName}</span>
          <Rating
            value={Number(r?.starRating) || 5}
            precision={0.5}
            readOnly
            size="small"
          />
        </div>
      </div>

      <div className="bdrv2-reviews-controls">
        <button
          type="button"
          className="bdrv2-reviews-arrow"
          aria-label="Previous review"
          onClick={() => go(idx - 1)}
        >
          &larr;
        </button>
        <div className="bdrv2-reviews-dots">
          {reviews.map((rv, i) => (
            <button
              key={rv._id || i}
              type="button"
              aria-label={`Review ${i + 1}`}
              className={`bdrv2-reviews-dot${i === idx ? " active" : ""}`}
              onClick={() => setIdx(i)}
            />
          ))}
        </div>
        <button
          type="button"
          className="bdrv2-reviews-arrow"
          aria-label="Next review"
          onClick={() => go(idx + 1)}
        >
          &rarr;
        </button>
      </div>
    </div>
  );
};

export default ReviewsCarouselV2;
